import ToDoListTaskTypeDto from "../../../../types/dtos/dashboards/right-bar/to-do-list/ToDoListTaskTypeDto";
import { useEffect, useMemo, useRef, useState } from "react";
import StandardToDoListItemDto from "../../../../types/dtos/dashboards/right-bar/to-do-list/StandardToDoListItemDto";
import { EditableTask } from "../../../../types/tasks/EditableTasks";
import ManageTaskPopup, { TaskPopupMode } from "../../../tasks/ManageTaskPopup";
import ModifyTaskResponseDto from "../../../../types/dtos/tasks/ModifyTaskResponseDto";
import myDashboardApi from "../../../../api/dashboard/myDashboardApi";
import { useAuth } from "react-oidc-context";
import ToDoListEmptyState from "../shared/ToDoListEmptyState";
import { advancedTaskHelper, taskTypeHelper } from "../../../../helpers";
import ToDoListDisplay from "../shared/ToDoListDisplay";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t } from "i18next";
import { ModifyStatus } from "../../../../types/generic";
import { LoadingPlaceholderItem } from "../shared/LoadingPlaceholderItem";
import ToDoListVerticalCarousel from "../shared/ToDoListVerticalCarousel";
import cx from "classnames";

interface StandardToDoListProps {
  initialToDoItems: StandardToDoListItemDto[];
  toDoListTaskTypes: ToDoListTaskTypeDto[];
  selectedTaskTypeId: number | undefined;
  hideTaskTypeBadge?: boolean | undefined;
  updateTaskTypeCount(taskTypeId: string | number, count: number): void;
  modifyTaskFromDashboard(
    task: any,
    onSuccess: (taskData: ModifyTaskResponseDto<number>) => void,
  ): void;
  onLoadTaskToEdit(
    taskId: number,
    taskType: string,
    successCallback: (responseData: any) => void,
  ): void;
  onTasksLoadingChange?(loading: boolean): void;
}

function StandardToDoList({
  initialToDoItems,
  toDoListTaskTypes,
  selectedTaskTypeId,
  hideTaskTypeBadge = false,
  updateTaskTypeCount,
  modifyTaskFromDashboard,
  onLoadTaskToEdit,
  onTasksLoadingChange,
}: StandardToDoListProps) {
  const auth = useAuth();
  const dashboardApi = new myDashboardApi(auth.user?.access_token);

  const tasksCacheRef = useRef<Map<number, StandardToDoListItemDto[]>>(new Map());
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [tasks, setTasks] = useState<StandardToDoListItemDto[]>([]);
  const [selectedTaskType, setSelectedTaskType] = useState<
    ToDoListTaskTypeDto | undefined
  >(undefined);
  const [taskToEdit, setTaskToEdit] = useState<EditableTask<number> | null>(
    null,
  );
  const [isReadOnlyPopup, setIsReadOnlyPopup] = useState<boolean>(false);
  const [taskLockedByJourney, setTaskLockedByJourney] = useState<string | null>(
    null,
  );
  const [popupMode, setPopupMode] = useState<TaskPopupMode>("ADD");
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const incompleteTasks = useMemo(
    () => tasks.filter((x) => !x.isCompleted),
    [tasks],
  );

  useEffect(() => {
    if (selectedTaskTypeId == undefined) {
      return;
    }

    const taskTypeChanged =
      selectedTaskType == undefined ||
      selectedTaskType.standardTaskTypeId !== selectedTaskTypeId;

    if (!taskTypeChanged) {
      return;
    }

    setSelectedTaskType(
      toDoListTaskTypes.find(
        (x) => x.standardTaskTypeId === selectedTaskTypeId,
      ),
    );

    if (isInitialLoad) {
      setIsInitialLoad(false);
      setTasks(initialToDoItems);
      tasksCacheRef.current.set(selectedTaskTypeId, initialToDoItems);
      return;
    }

    const cached = tasksCacheRef.current.get(selectedTaskTypeId);
    if (cached !== undefined) {
      setTasks(cached);
      setIsRefreshing(true);
    } else {
      setTasks([]);
      setIsLoading(true);
    }
    onTasksLoadingChange?.(true);

    dashboardApi.getStandardToDoListItems(
      selectedTaskTypeId,
      (data: StandardToDoListItemDto[]) => {
        tasksCacheRef.current.set(selectedTaskTypeId, data);
        setIsLoading(false);
        setIsRefreshing(false);
        setTasks(data);
        onTasksLoadingChange?.(false);
      },
      (error: any) => {
        console.error(error.message);
        setIsLoading(false);
        setIsRefreshing(false);
        onTasksLoadingChange?.(false);
      },
    );
  }, [selectedTaskTypeId]);

  const onModalOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setTaskToEdit(null);
      setIsReadOnlyPopup(false);
    }
    setModalIsOpen(isOpen);
  };

  const onAddTaskPopup = () => {
    setPopupMode("ADD");
    setModalIsOpen(true);
  };

  const onEditTaskPopup = (taskId: number) => {
    if (selectedTaskType != undefined) {
      const successCallback = (responseData: ModifyTaskResponseDto<number>) => {
        setPopupMode("EDIT");
        setModalIsOpen(true);
        setTaskToEdit(responseData.task);
        setTaskLockedByJourney(responseData.editLockedByJourney);

        if (
          responseData.task &&
          (responseData.task.status === "COMPLETED" ||
            responseData.task.status === "CANCELLED" ||
            responseData.editLockedByJourney)
        ) {
          setIsReadOnlyPopup(true);
        } else {
          setIsReadOnlyPopup(false);
        }
      };
      onLoadTaskToEdit(
        taskId,
        selectedTaskType.standardTaskType,
        successCallback,
      );
    }
  };

  const handleTaskSave = (task: EditableTask<number>) => {
    modifyTaskFromDashboard(task, (returnedTask) => {
      if (returnedTask.task.taskId != null) {
        refreshTaskData(returnedTask.task.taskId, task.modifyStatus);
      }
    });
  };

  const refreshTaskData = (taskId: number, taskModifyStatus: ModifyStatus) => {
    if (selectedTaskTypeId != undefined) {
      dashboardApi.getStandardToDoListItems(
        selectedTaskTypeId,
        (data: StandardToDoListItemDto[]) => {
          data = updateDataWithRelevantFlags(taskId, taskModifyStatus, data);
          if (selectedTaskTypeId != undefined) {
            tasksCacheRef.current.set(selectedTaskTypeId, data);
          }
          setIsLoading(false);
          setIsRefreshing(false);
          setTasks(data);
          updateTaskTypeCount(
            selectedTaskTypeId,
            data.filter((x) => !x.isCompleted).length,
          );
        },
        (error: any) => {
          console.error(error.message);
          setIsLoading(false);
        },
      );
    }
  };

  const updateDataWithRelevantFlags = (
    taskId: number,
    taskModifyStatus: ModifyStatus,
    data: StandardToDoListItemDto[],
  ) => {
    let hasBeenAddedFlagSet = false;
    let hasUpdatedFlagBeenSet = false;

    if (taskModifyStatus === "NEW") {
      hasBeenAddedFlagSet = true;
      data = data.map((x) => {
        if (x.id === taskId) {
          x.wasAdded = true;
        }
        return x;
      });
    }

    if (taskModifyStatus !== "NEW") {
      hasUpdatedFlagBeenSet = true;
      data = data.map((x) => {
        if (x.id === taskId) {
          x.wasUpdated = true;
        }
        return x;
      });
    }

    if (hasBeenAddedFlagSet || hasUpdatedFlagBeenSet) {
      setTimeout(() => {
        setTasks((prev) =>
          prev.map((x) => ({
            ...x,
            wasAdded: x.id === taskId ? false : x.wasAdded,
            wasUpdated: x.id === taskId ? false : x.wasUpdated,
          })),
        );
      }, 2000);
    }
    return data;
  };

  const manageTaskPopupRefresh = () => {
    if (taskToEdit != undefined && taskToEdit.taskId != null) {
      refreshTaskData(taskToEdit.taskId, taskToEdit.modifyStatus);
    }
  };

  const showSkeleton = isLoading && tasks.length === 0;

  return (
    <>
      <div className="flex flex-col h-full">
        {showSkeleton && (
          <>
            <LoadingPlaceholderItem />
            <LoadingPlaceholderItem />
            <LoadingPlaceholderItem />
          </>
        )}

        {selectedTaskType && !showSkeleton && (
          <div
            className={cx(
              "transition-opacity duration-200 ease-out",
              isRefreshing && "opacity-70 pointer-events-none",
            )}
          >
            {incompleteTasks.length === 0 && (
              <ToDoListEmptyState
                iconType={taskTypeHelper.getTaskTypeIcon(
                  selectedTaskType.standardTaskType,
                )}
                pluralNameTranslationKeyIdentifier={
                  selectedTaskType.pluralTranslationKeyIdentifier
                }
              />
            )}
            {incompleteTasks.length > 0 && (
              <ToDoListVerticalCarousel
                items={incompleteTasks}
                resetKey={selectedTaskTypeId}
                renderItems={(visibleItems) => (
                  <ToDoListDisplay
                    hideTaskTypeBadge={hideTaskTypeBadge}
                    items={visibleItems}
                    iconType={taskTypeHelper.getTaskTypeIcon(
                      selectedTaskType.standardTaskType,
                    )}
                    singularNameTranslationKeyIdentifier={
                      selectedTaskType.singularTranslationKeyIdentifier
                    }
                    onItemClick={onEditTaskPopup}
                  />
                )}
              />
            )}
            {selectedTaskType && (
              <>
                <hr className="border-gray-200 mt-2 mb-3" />
                <div className="flex justify-center">
                  <button
                    className="inline-flex items-center primary-colour font-semibold text-sm bg-transparent border-0 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={onAddTaskPopup}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span className="ml-2">
                      {t("TaskType.Button.AddNewX").replace(
                        "#TASK_TYPE#",
                        advancedTaskHelper.ToLowerCase(
                          selectedTaskType.singularTranslationKeyIdentifier,
                        ),
                      )}
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {selectedTaskType && (
        <ManageTaskPopup
          mode={popupMode}
          isOpen={modalIsOpen}
          onOpenChange={onModalOpenChange}
          onSaveClick={handleTaskSave}
          taskType={selectedTaskType.standardTaskType}
          editObject={taskToEdit}
          popupOrigin="DASHBOARD"
          refreshParentTaskList={manageTaskPopupRefresh}
          isReadOnly={isReadOnlyPopup}
          setIsReadOnly={setIsReadOnlyPopup}
          taskLockedByJourney={taskLockedByJourney}
        />
      )}
    </>
  );
}

export default StandardToDoList;
